import { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ErrorToast,
  Confirmation,
  SuccessToast,
} from "../../../../Utils/SweetAlert";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Formik } from "formik";
import SearchBox from "../../../../Components/SearchBox";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  useMutateDeleteWelfare,
  useMutateDragDropWelfare,
} from "../../hooks/useApiHook";
import "../../style.css";
import { GetWelfareList } from "../../../../Api/welfare";
import { WelfareSearchBarInitialValues } from "../../constant";
import { IoEye } from "react-icons/io5";
import { EditButton, DeleteButton, AddButton } from "../../../../Components";

const WelfareList = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  const deleteWelfare = useMutateDeleteWelfare();
  const dragdropWelfare = useMutateDragDropWelfare();
  const [WelfareList, setWelfareList] = useState(null);

  const {
    isFetching: isWelfareLoading,
    data: initialData,
    refetch,
  } = useQuery({
    queryKey: ["get-welfare-list"],
    queryFn: () =>
      GetWelfareList({
        search: formRef?.current?.values?.search,
      }),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server Error.");
    },
  });

  useEffect(() => {
    if (initialData) {
      setWelfareList(initialData);
    }
  }, [initialData]);

  const handleDeleteWelfare = (id) => {
    Confirmation("Are you sure you want to delete Welfare?").then(async (deleteConfirm) => {
      if (deleteConfirm.isConfirmed) {
        deleteWelfare.mutate(id, {
          onSuccess: (response) => {
            SuccessToast(response?.message || "Delete Successful.");
            refetch();
          },
          onError: (error) => {
            ErrorToast(error?.response?.data?.message || "Server Error.");
          },
        });
      }
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const { source, destination } = result;

    const items = Array.from(WelfareList.data);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);
    setWelfareList({ ...WelfareList, data: items });

    dragdropWelfare.mutate(
      {
        welfareId: reorderedItem._id,
        newOrder: destination.index,
      },
      {
        onSuccess: () => {
          refetch();
        },
        onError: (error) => {
          ErrorToast(error?.response?.data?.message || "Failed To Reorder.");
        },
      }
    );
  };

  return (
    <>
      <Helmet>
        <title>OVE | Admin Welfare</title>
      </Helmet>
      <div className="page-wrapper admin-user">
        <div className="content container-fluid">
          <div className="client-wrapper">
            <div className="page-header">
              <div className="col">
                <h1 className="page-title">Welfare</h1>
              </div>
              <div className="col-12 col-md-3 col-lg-2 px-2">
                <Formik
                  initialValues={WelfareSearchBarInitialValues}
                  innerRef={formRef}
                >
                  {() => {
                    return (
                      <form>
                        <SearchBox refetch={refetch} setPage={() => { }} />
                      </form>
                    );
                  }}
                </Formik>
              </div>
              <div>
                <AddButton
                  to="/admin/add-welfare"
                  permission="create-welfare"
                />
              </div>
            </div>
            <div className="col-sm-12 table-width">
              <div className="cards-table card-table">
                <div className="card-body">
                  <div className="table-responsive custom-pagination">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <table className="table datatable welfare-table-list table-manager mb-0" style={{ width: '100%' }}>
                        <thead className="thead-light">
                          <tr>
                            <th className="col-name text-start" style={{ width: '70%' }}>Name</th>
                            <th className="col-action text-center" style={{ width: '30%' }}>Action</th>
                          </tr>
                        </thead>
                        {isWelfareLoading && (
                          <tbody>
                            <tr>
                              <td align="center" colSpan={2}>
                                <div className="d-flex justify-content-center align-items-center py-4">
                                  <div className="spinner-border loader-theme" role="status" />
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        )}
                        {!isWelfareLoading && (
                          <Droppable droppableId="droppable">
                            {(provided) => (
                              <tbody
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {WelfareList?.data.map((item, index) => {
                                  return (
                                    <Draggable
                                      key={item._id}
                                      draggableId={item._id}
                                      index={index}
                                    >
                                      {(provided) => (
                                        <tr
                                          key={index}
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          className="table"
                                        >
                                          <td className="col-name">
                                            {item.name}
                                          </td>
                                          <td className="category-td-padding-remove col-action text-center">
                                            <EditButton
                                              to="/admin/edit-welfare"
                                              state={{ id: item?._id }}
                                              permission="edit-welfare"
                                              disabled={item?.role === "superadmin"}
                                            />
                                            <DeleteButton
                                              onDelete={() => handleDeleteWelfare(item?._id)}
                                              permission="delete-welfare"
                                              disabled={item?.role === "superadmin"}
                                            />
                                            <Tooltip
                                              title="View"
                                              className="btn btn-sm btn-white text-success insight-stickey-actions-btn"
                                              onClick={() =>
                                                navigate("/admin/welfare-detail", {
                                                  state: { id: item?._id },
                                                })
                                              }
                                            >
                                              <IconButton>
                                                <IoEye className="text-secondary icon-color p-0" />
                                              </IconButton>
                                            </Tooltip>
                                          </td>
                                        </tr>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {WelfareList?.data?.length === 0 && (
                                  <tr>
                                    <td className="text-center" colSpan={2}>
                                      No Records Found.
                                    </td>
                                  </tr>
                                )}
                                {provided.placeholder}


                              </tbody>
                            )}
                          </Droppable>
                        )}
                      </table>
                    </DragDropContext>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WelfareList;
